import React, { useEffect, useState } from "react";
import { fetchLedgerRows } from "./services/exportJobsService";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

function formatTimestamp(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function formatWindowDate(ts) {
  if (!ts) return "—";
  return String(ts).slice(0, 10);
}

// Portal modal listing export_job_ledger rows for one execution: source
// event id, delivery status badge, error_detail (failed rows only), sent_at.
// Modeled on CustomerTimelineModal.jsx's portal + per-event drill-down.
export function ExecutionLedgerModal({ execution, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const motion = useModalMotion(onClose, { closeOnBackdrop: true });
  const { requestClose } = motion;

  useEffect(() => {
    if (!execution?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchLedgerRows(execution.id, page).then(({ data, error: err, count: total }) => {
      if (cancelled) return;
      if (err) setError(err.message);
      setRows(data || []);
      setCount(total ?? 0);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [execution?.id, page]);

  const hasNextPage = (page + 1) * 50 < count;

  return (
    <ModalShell motion={motion} labelledBy="execution-ledger-title" maxWidth={640} panelStyle={{ overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
        <div style={{ padding: "22px 24px 16px", borderBottom: "1px solid #F0F0F0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div id="execution-ledger-title" style={{ fontSize: 11, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Execution Ledger</div>
            <div style={{ fontWeight: 600, color: "#151515", fontSize: 28, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>{formatTimestamp(execution?.started_at || execution?.created_at)}</div>
            {/* A re-run's sent_at is genuinely today — this states the data
                period it actually covers so that never reads as an
                anomaly (design Decision 3). */}
            {execution?.run_type === "manual_rerun" && execution?.export_job_reruns && (
              <div style={{ fontSize: 12, color: "#6C63FF", marginTop: 4 }}>
                Re-run covering {formatWindowDate(execution.export_job_reruns.window_from)} – {formatWindowDate(execution.export_job_reruns.window_to)} (billing verdict time)
              </div>
            )}
          </div>
          <button onClick={requestClose} aria-label="Close" style={{ background: "none", border: "none", color: "#626262", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
          {loading && <div style={{ color: "#7b7b7b", fontSize: 13 }}>Loading ledger…</div>}
          {error && <div style={{ color: "#e53935", fontSize: 13 }}>{error}</div>}
          {!loading && !error && rows.length === 0 && <div style={{ color: "#7b7b7b", fontSize: 13 }}>No ledger rows for this execution.</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {rows.map((row, i) => {
              const isFailed = row.status === "failed";
              return (
                <div key={row.id} style={{ padding: "12px 0", borderBottom: i < rows.length - 1 ? "1px solid #F0F0F0" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "#151515", fontFamily: "monospace" }}>{row.source_event_id}</span>
                    <span style={{ fontSize: 12, padding: "2px 7px", borderRadius: 20, background: isFailed ? "#FFF5F5" : "#d8fffa", color: isFailed ? "#e53935" : "#208a3e", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                      {row.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#7b7b7b", marginTop: 4 }}>{formatTimestamp(row.sent_at)}</div>
                  {isFailed && row.error_detail && (
                    <div style={{ fontSize: 13, color: "#e53935", marginTop: 4, background: "#FFF5F5", borderRadius: 6, padding: "6px 10px" }}>{row.error_detail}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: "12px 24px", borderTop: "1px solid #F0F0F0", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: "7px 14px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: page === 0 ? "#B8B8B8" : "#626262", fontSize: 11, cursor: page === 0 ? "default" : "pointer" }}>Previous</button>
          <span style={{ fontSize: 11, color: "#7b7b7b" }}>Page {page + 1}</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={!hasNextPage} style={{ padding: "7px 14px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: !hasNextPage ? "#B8B8B8" : "#626262", fontSize: 11, cursor: !hasNextPage ? "default" : "pointer" }}>Next</button>
        </div>
    </ModalShell>
  );
}
